/** Quote Request Details — structured-extraction block on EmailReader.
 *
 *  Email→Quote Orchestration ARC § Phase 8.
 *
 *  Renders the LLM-extracted address + intent + scope summary +
 *  building profile for emails the AI classified as
 *  `category='quote_request'`. Mounted as a sibling above the
 *  external_links + attachments blocks so the operator sees the
 *  scope at-a-glance before they scroll into the body.
 *
 *  Visual states:
 *    - `pending` / `extracting` → muted skeleton block with a
 *      spinning glyph + "Extracting scope of work…" message; the
 *      enclosing `useEmail()` polls every 3s and re-renders when
 *      the row transitions to `done` / `partial` / `failed`.
 *    - `done`     → full details: address chip, intent badge,
 *      sqm/storeys/beds/baths, trades + materials list, markdown
 *      scope summary, deep-link to the auto-spawned QDrive folder.
 *    - `partial`  → same as `done` but with a yellow "Some fields
 *      missing — feel free to edit" banner.
 *    - `failed`   → red banner with the operator-visible
 *      `last_error` message + a "Retry" hint (the operator can
 *      re-classify the email to re-fire the chain).
 *    - `skipped`  → muted "Extraction skipped — body too short" hint.
 *
 *  Deep-link:
 *    `qdrive_folder_id` (when set) opens `/qdrive?folder=<id>` —
 *    the canonical QDrive URL the rest of the app uses (see P4
 *    AttachmentChip / AttachmentList).
 */

import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Building2,
  Loader2,
  FolderOpen,
  AlertTriangle,
  CircleCheck,
  CircleAlert,
  Hammer,
  Boxes,
  Ruler,
  Mail,
  CheckCircle2,
  Clock,
  Copy as CopyIcon,
} from 'lucide-react'
import { cn } from '../../_internal/lib/utils'
import { useToast } from '../../_internal/components/Toast'
import { emailApi } from '../lib/api'
import type { QuoteRequestExtraction } from '../hooks/useEmails'

interface QuoteRequestDetailsBlockProps {
  extraction: QuoteRequestExtraction
  /** UUID of the parent email — required to call the mark-used
   *  endpoint when the operator clicks "Use this draft". */
  emailId: string
  /** Optional callback fired with the draft body + subject when the
   *  operator clicks "Use this draft". Hosts pass this to seed the
   *  compose dock with the LLM text. If omitted, the button just
   *  copies the body to the clipboard + marks the row as USED. */
  onUseDraftReply?: (args: { subject: string; body: string }) => void
  /** Slug emitted by the host app so we can build cross-tenant
   *  `/<tenant>/qdrive?folder=<id>` deep-links. Defaults to ''
   *  which produces `/qdrive?folder=<id>` (master Quotech path). */
  tenantPathPrefix?: string
}

const INTENT_LABEL: Record<QuoteRequestExtraction['intent'], string> = {
  new_build: 'New build',
  renovation: 'Renovation',
  extension: 'Extension',
  repair: 'Repair',
  remedial: 'Remedial / waterproofing',
  fitout: 'Fit-out',
  other: 'Other',
  unknown: 'Unknown',
}

const INTENT_TONE: Record<QuoteRequestExtraction['intent'], string> = {
  new_build: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20',
  renovation: 'bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/20',
  extension: 'bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 border-indigo-500/20',
  repair: 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/20',
  remedial: 'bg-orange-500/10 text-orange-700 dark:text-orange-300 border-orange-500/20',
  fitout: 'bg-purple-500/10 text-purple-700 dark:text-purple-300 border-purple-500/20',
  other: 'bg-muted text-muted-foreground border-border',
  unknown: 'bg-muted text-muted-foreground border-border',
}

export function QuoteRequestDetailsBlock({
  extraction,
  emailId,
  onUseDraftReply,
  tenantPathPrefix = '',
}: QuoteRequestDetailsBlockProps) {
  const navigate = useNavigate()
  const { toast } = useToast()
  const [draftMarking, setDraftMarking] = useState(false)
  // Optimistic-mark — once the operator clicks "Use this draft" the
  // chip flips to USED locally so the UI is responsive while the
  // POST round-trips. If the POST fails we revert + surface a toast.
  const [optimisticUsed, setOptimisticUsed] = useState(false)

  // -------------------------------------------------------------
  // Hooks-rule discipline: every hook MUST be declared before any
  // status-driven early return. Previously the two `useCallback`s
  // below lived after `if (isInflight) return …` / `if (status ===
  // 'failed') return …` / `if (status === 'skipped') return null` /
  // `if (!isTerminal) return null` — so when the extraction
  // transitioned from `extracting` → `done` via the 3s poll the
  // hook count jumped from 4 → 6 and React threw error #300
  // ("Rendered fewer hooks than expected"). Hoisting these here
  // keeps the hook count constant across every render path.
  //
  // The dependencies reference `extraction.draft_reply_*` /
  // `extraction.draft_reply_text` — those fields are NULL on the
  // early-return paths (extracting / failed / skipped), so the
  // closures still compute correct values but their bodies bail
  // out on `if (!draftIsReady …) return` / `if (!draftBody) return`
  // before doing any work.
  const draftBodyForHandler = extraction.draft_reply_text || ''
  const draftSubjectForHandler = extraction.draft_reply_subject || ''
  const draftIsReadyForHandler = extraction.draft_reply_status === 'ready'

  const handleUseDraft = useCallback(async () => {
    if (!draftIsReadyForHandler || draftMarking) return
    setDraftMarking(true)
    setOptimisticUsed(true)
    try {
      if (onUseDraftReply) {
        onUseDraftReply({
          subject: draftSubjectForHandler,
          body: draftBodyForHandler,
        })
      } else {
        try {
          await navigator.clipboard.writeText(draftBodyForHandler)
          toast(
            "Paste it into your reply. Don't forget to swap the {{ESTIMATED_TURNAROUND}} placeholder.",
            'success',
            { title: 'Draft copied to clipboard' },
          )
        } catch {
          // clipboard API blocked — silent (the toast below shows
          // server-side status instead)
        }
      }

      await emailApi(
        `/api/emails/${emailId}/quote-request-draft-reply/mark-used/`,
        { method: 'POST' },
      )
    } catch (err) {
      setOptimisticUsed(false)
      const msg = err instanceof Error ? err.message : String(err)
      toast(msg.slice(0, 200), 'error', {
        title: 'Could not mark draft as used',
      })
    } finally {
      setDraftMarking(false)
    }
  }, [
    draftIsReadyForHandler,
    draftMarking,
    draftBodyForHandler,
    draftSubjectForHandler,
    emailId,
    onUseDraftReply,
    toast,
  ])

  const handleCopyDraft = useCallback(async () => {
    if (!draftBodyForHandler) return
    try {
      await navigator.clipboard.writeText(draftBodyForHandler)
      toast('Paste into your reply window.', 'success', {
        title: 'Draft copied',
      })
    } catch {
      toast('Browser permissions prevented copy.', 'error', {
        title: 'Clipboard blocked',
      })
    }
  }, [draftBodyForHandler, toast])

  const isInflight = extraction.status === 'pending' || extraction.status === 'extracting'
  const isTerminal =
    extraction.status === 'done' ||
    extraction.status === 'partial' ||
    extraction.status === 'failed' ||
    extraction.status === 'skipped'

  // --- IN-FLIGHT ----------------------------------------------
  if (isInflight) {
    return (
      <div
        className={cn(
          'rounded-xl border border-border bg-gradient-to-b from-emerald-500/[0.04] to-transparent',
          'p-4 mb-4',
        )}
        data-testid="quote-request-extracting-block"
      >
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0">
            <Loader2 className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400 animate-spin" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground leading-tight">
              Extracting scope of work…
            </p>
            <p className="text-[11px] text-muted-foreground leading-tight">
              Reading the email + attachments to pull address, intent, and trade requirements.
            </p>
          </div>
        </div>
      </div>
    )
  }

  // --- TERMINAL ERROR-ish states -------------------------------
  if (extraction.status === 'failed') {
    return (
      <div
        className={cn(
          'rounded-xl border border-rose-500/40 bg-rose-500/[0.04]',
          'p-4 mb-4',
        )}
        data-testid="quote-request-failed-block"
      >
        <div className="flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-rose-600 dark:text-rose-400 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground leading-tight">
              Quote-request extraction failed
            </p>
            <p className="text-[11px] text-muted-foreground leading-tight mt-1">
              {extraction.last_error || 'The LLM extractor returned an unparseable response.'}
            </p>
          </div>
        </div>
      </div>
    )
  }

  if (extraction.status === 'skipped') {
    return null
  }

  if (!isTerminal) {
    return null
  }

  const intentClass = INTENT_TONE[extraction.intent] || INTENT_TONE.unknown
  const intentLabel = INTENT_LABEL[extraction.intent] || INTENT_LABEL.unknown

  const profile: Array<{ label: string; value: string }> = []
  if (extraction.square_meters)
    profile.push({ label: 'm²', value: `${extraction.square_meters}` })
  if (extraction.storeys) profile.push({ label: 'storeys', value: `${extraction.storeys}` })
  if (extraction.bedrooms) profile.push({ label: 'bed', value: `${extraction.bedrooms}` })
  if (extraction.bathrooms) profile.push({ label: 'bath', value: `${extraction.bathrooms}` })
  if (extraction.rooms_other_json) {
    for (const [label, count] of Object.entries(extraction.rooms_other_json)) {
      if (typeof count === 'number' && count > 0) {
        profile.push({ label, value: `${count}` })
      }
    }
  }

  const trades = Array.isArray(extraction.trades_required_json)
    ? extraction.trades_required_json
    : []
  const materials = Array.isArray(extraction.materials_required_json)
    ? extraction.materials_required_json
    : []

  const goToQDriveFolder = () => {
    if (!extraction.qdrive_folder_id) return
    const path = `${tenantPathPrefix}/qdrive?folder=${encodeURIComponent(extraction.qdrive_folder_id)}`
    navigate(path)
  }

  // Phase 11 — pure render-time derivations for the draft-reply chip
  // and preview. The two handler closures (`handleUseDraft`,
  // `handleCopyDraft`) are declared near the top of this component
  // BEFORE the status-driven early returns — they read the same
  // extraction fields via local `…ForHandler` aliases so the hook
  // count stays constant across `extracting` → `done` transitions.
  // See the hoisting comment at the top for why this matters.
  const draftBody = draftBodyForHandler
  const draftSubject = draftSubjectForHandler
  const draftIsReady = draftIsReadyForHandler
  const draftIsUsed =
    optimisticUsed || extraction.draft_reply_status === 'used'
  const draftIsInflight =
    extraction.draft_reply_status === 'pending' ||
    extraction.draft_reply_status === 'generating'
  const draftIsFailed = extraction.draft_reply_status === 'failed'

  return (
    <div
      className={cn(
        'rounded-xl border border-emerald-500/30 bg-gradient-to-b from-emerald-500/[0.06] to-transparent',
        'p-4 mb-4',
      )}
      data-testid="quote-request-details-block"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="h-7 w-7 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0">
            <Building2 className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground leading-tight truncate">
              Quote Request Details
            </p>
            <p className="text-[11px] text-muted-foreground leading-tight">
              Auto-extracted scope of work · review + edit before quoting
            </p>
          </div>
        </div>
        <span
          className={cn(
            'inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium',
            intentClass,
          )}
          data-testid="quote-request-intent-badge"
        >
          {intentLabel}
        </span>
      </div>

      {/* Partial-status banner */}
      {extraction.status === 'partial' && (
        <div
          className={cn(
            'flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/[0.06] px-2.5 py-1.5 mb-3',
          )}
          data-testid="quote-request-partial-banner"
        >
          <CircleAlert className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
          <p className="text-[11px] text-foreground/80 leading-snug">
            Some fields are missing - the email didn&apos;t give us everything.
            Edit inline (coming soon) or fill the rest into the Quote Tracker
            row directly.
          </p>
        </div>
      )}

      {/* Address + QDrive deep-link */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0 flex-1">
          <p className="text-[11px] text-muted-foreground uppercase tracking-wide mb-0.5">
            Site address
          </p>
          <p
            className="text-sm font-medium text-foreground leading-snug truncate"
            data-testid="quote-request-address"
          >
            {extraction.address_short ||
              extraction.address_raw ||
              <span className="italic text-muted-foreground">No address detected</span>}
          </p>
          {(extraction.address_suburb ||
            extraction.address_state ||
            extraction.address_postcode) && (
            <p className="text-[11px] text-muted-foreground leading-tight">
              {[
                extraction.address_suburb,
                extraction.address_state,
                extraction.address_postcode,
              ]
                .filter(Boolean)
                .join(' ')}
            </p>
          )}
        </div>
        {extraction.qdrive_folder_id && (
          <button
            type="button"
            onClick={goToQDriveFolder}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-md border border-emerald-500/30 bg-emerald-500/10',
              'px-2.5 py-1 text-[11px] font-medium text-emerald-700 dark:text-emerald-300',
              'hover:bg-emerald-500/20 transition shrink-0',
            )}
            data-testid="quote-request-qdrive-deep-link"
            title={extraction.qdrive_folder_path || 'Open in QDrive'}
          >
            <FolderOpen className="h-3 w-3" />
            <span>
              QDrive · {extraction.files_classified_count ?? 0} file
              {(extraction.files_classified_count ?? 0) === 1 ? '' : 's'}
            </span>
          </button>
        )}
      </div>

      {/* Building profile (sqm / storeys / beds / baths / other rooms) */}
      {profile.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3" data-testid="quote-request-profile">
          {profile.map((p) => (
            <span
              key={`${p.label}-${p.value}`}
              className={cn(
                'inline-flex items-center gap-1 rounded-md bg-muted/60 border border-border',
                'px-2 py-0.5 text-[11px] text-foreground',
              )}
            >
              <Ruler className="h-3 w-3 text-muted-foreground" />
              <strong className="font-semibold">{p.value}</strong>
              <span className="text-muted-foreground">{p.label}</span>
            </span>
          ))}
        </div>
      )}

      {/* Trades + materials capacity map */}
      {(trades.length > 0 || materials.length > 0) && (
        <div className="grid gap-2 sm:grid-cols-2 mb-3">
          {trades.length > 0 && (
            <div
              className="rounded-md border border-border bg-background/60 p-2"
              data-testid="quote-request-trades"
            >
              <div className="flex items-center gap-1.5 mb-1">
                <Hammer className="h-3 w-3 text-muted-foreground" />
                <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
                  Trades required
                </p>
              </div>
              <div className="flex flex-wrap gap-1">
                {trades.map((t) => (
                  <span
                    key={t}
                    className="inline-flex rounded bg-muted px-1.5 py-0.5 text-[11px] text-foreground"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          )}
          {materials.length > 0 && (
            <div
              className="rounded-md border border-border bg-background/60 p-2"
              data-testid="quote-request-materials"
            >
              <div className="flex items-center gap-1.5 mb-1">
                <Boxes className="h-3 w-3 text-muted-foreground" />
                <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
                  Materials
                </p>
              </div>
              <div className="flex flex-wrap gap-1">
                {materials.map((m) => (
                  <span
                    key={m}
                    className="inline-flex rounded bg-muted px-1.5 py-0.5 text-[11px] text-foreground"
                  >
                    {m}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Scope summary — markdown bullets */}
      {extraction.scope_summary && (
        <div className="rounded-md border border-border bg-background/40 p-3">
          <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-1.5">
            Scope of works
          </p>
          <pre
            className={cn(
              'whitespace-pre-wrap font-sans text-[12.5px] leading-relaxed',
              'text-foreground/90',
            )}
            data-testid="quote-request-scope-summary"
          >
            {extraction.scope_summary}
          </pre>
        </div>
      )}

      {/* Phase 11 — auto-drafted acknowledgement reply.
          States:
            - in-flight ('pending' | 'generating') → muted "Drafting reply…" chip
            - ready                               → preview + "Use this draft" button
            - used                                → green "Used" chip + timestamp
            - failed                              → red banner + last_error */}
      {draftIsInflight && (
        <div
          className={cn(
            'mt-3 rounded-md border border-border bg-background/60 p-2',
            'flex items-center gap-2',
          )}
          data-testid="quote-request-draft-reply-inflight"
        >
          <Loader2 className="h-3.5 w-3.5 text-muted-foreground animate-spin" />
          <p className="text-[11px] text-muted-foreground leading-tight">
            Drafting acknowledgement reply…
          </p>
        </div>
      )}

      {draftIsFailed && (
        <div
          className={cn(
            'mt-3 rounded-md border border-rose-500/30 bg-rose-500/[0.04] p-2',
            'flex items-start gap-2',
          )}
          data-testid="quote-request-draft-reply-failed"
        >
          <AlertTriangle className="h-3.5 w-3.5 text-rose-600 dark:text-rose-400 shrink-0 mt-0.5" />
          <p className="text-[11px] text-foreground/80 leading-snug">
            Couldn&apos;t draft a reply automatically
            {extraction.draft_reply_error
              ? ` - ${extraction.draft_reply_error.slice(0, 200)}`
              : '.'}
          </p>
        </div>
      )}

      {(draftIsReady || draftIsUsed) && draftBody && (
        <div
          className={cn(
            'mt-3 rounded-md border bg-background/60 p-3',
            draftIsUsed
              ? 'border-emerald-500/40 bg-emerald-500/[0.04]'
              : 'border-border',
          )}
          data-testid="quote-request-draft-reply-ready"
        >
          <div className="flex items-center justify-between gap-2 mb-1.5">
            <div className="flex items-center gap-1.5">
              <Mail className="h-3 w-3 text-muted-foreground" />
              <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
                Suggested acknowledgement reply
              </p>
            </div>
            {draftIsUsed && (
              <span
                className={cn(
                  'inline-flex items-center gap-1 text-[10.5px]',
                  'text-emerald-700 dark:text-emerald-300',
                )}
                data-testid="quote-request-draft-reply-used-chip"
                title={
                  extraction.draft_reply_used_at
                    ? new Date(extraction.draft_reply_used_at).toLocaleString()
                    : undefined
                }
              >
                <CheckCircle2 className="h-3 w-3" />
                Used
              </span>
            )}
          </div>
          {draftSubject && (
            <p className="text-[12px] font-medium text-foreground/90 mb-1">
              {draftSubject}
            </p>
          )}
          <pre
            className={cn(
              'whitespace-pre-wrap font-sans text-[12.5px] leading-relaxed',
              'text-foreground/90 mb-2',
            )}
            data-testid="quote-request-draft-reply-body"
          >
            {draftBody}
          </pre>
          {draftBody.includes('{{ESTIMATED_TURNAROUND}}') && (
            <p
              className="flex items-center gap-1 text-[10.5px] text-muted-foreground mb-2"
              data-testid="quote-request-draft-reply-turnaround-hint"
            >
              <Clock className="h-3 w-3" />
              Replace <code className="bg-muted px-1 py-0.5 rounded">{`{{ESTIMATED_TURNAROUND}}`}</code> before sending
              (the upcoming time-bucket picker will fill it automatically).
            </p>
          )}
          <div className="flex items-center justify-end gap-1.5">
            <button
              type="button"
              onClick={handleCopyDraft}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-md border border-border bg-background',
                'px-2 py-1 text-[11px] font-medium text-foreground/80',
                'hover:bg-muted transition',
              )}
              data-testid="quote-request-draft-reply-copy"
            >
              <CopyIcon className="h-3 w-3" />
              Copy
            </button>
            <button
              type="button"
              onClick={handleUseDraft}
              disabled={!draftIsReady || draftMarking}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-md',
                'px-2.5 py-1 text-[11px] font-medium transition',
                draftIsReady
                  ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                  : 'bg-muted text-muted-foreground cursor-not-allowed',
              )}
              data-testid="quote-request-draft-reply-use"
            >
              <Mail className="h-3 w-3" />
              {draftIsUsed ? 'Re-use this draft' : 'Use this draft'}
            </button>
          </div>
        </div>
      )}

      {/* Confidence chip — bottom-right corner */}
      {typeof extraction.confidence === 'number' && (
        <div className="flex items-center justify-end mt-2">
          <span
            className={cn(
              'inline-flex items-center gap-1 text-[10.5px] text-muted-foreground',
            )}
            title={`LLM-self-reported confidence: ${(extraction.confidence * 100).toFixed(0)}%`}
            data-testid="quote-request-confidence"
          >
            <CircleCheck className="h-3 w-3" />
            confidence {Math.round(extraction.confidence * 100)}%
          </span>
        </div>
      )}
    </div>
  )
}
